import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from "@angular/core";
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from "@angular/forms";
import {
    MatAutocomplete,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Product } from "app/entities";
import { Item } from "app/entities/item";
import { Unit } from "app/entities/unit";
import { ProductsService } from "app/main/pages/products/product.service";
import { AlertService } from "app/services/alert.service";
import { LocalStorageService } from "app/services/local-storage.service";
import { PagedAutocompleteController, PagedAutocompleteService } from "app/services/paged_autocomplete_service";
import { SingletonsService } from "app/services/singletons.service";
import { isCompactViewport } from "app/helpers/oa-dialog";
import { ItemDetailsField, ProductStockInfo, filterCategories, isNewCategory, plainDescription, productStockInfo } from "app/helpers/item-details";
import { ProposalService } from "app/main/pages/proposals/proposals.service";

import { BehaviorSubject, ReplaySubject, Subject } from "rxjs";
import {
    finalize,
    takeUntil,
} from "rxjs/operators";

/** Opção "Adicionar “…” como item novo" (mesmo valor do cartão de itens) */
const NEW_ITEM_OPTION = '__orca_new_item__';

@Component({
    selector: "app-proposal-add-item",
    templateUrl: "./proposal-add-item.component.html",
    styleUrls: ["./proposal-add-item.component.scss"],
})
export class ProposalAddItemComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('auto', { static: true }) auto!: MatAutocomplete;
    @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
    @ViewChild('itemName', { read: ElementRef }) itemName!: ElementRef<HTMLInputElement>;
    @ViewChild('suggestionsList') suggestionsList?: ElementRef<HTMLElement>;

    form: FormGroup;
    item: Item;
    index: number;

    units: Unit[] = [];
    defaultUnit: Unit | null = null;

    // Autocomplete paginado
    filteredProducts = new BehaviorSubject<Product[]>([]);
    accumulated: Product[] = [];

    isLoading: boolean = false;

    // Telas compactas: diálogo em tela cheia e sugestões de produto em linha
    // (o painel do autocomplete cobria os demais campos e o teclado virtual).
    readonly compact = isCompactViewport();
    suggestionsOpen = false;

    readonly NEW_ITEM_OPTION = NEW_ITEM_OPTION;

    // ORCA-1945: todos os campos configurados para exibição na mesma tela
    fields: Record<ItemDetailsField, boolean> = { image: false, description: false, category: false, unit: true, unit_price: true, code: false };
    detailsOn = true;
    canSaveProduct = true;
    categories: string[] = [];
    uploading = false;
    /** Escolhido do cadastro nesta tela (aviso "Preenchido com o cadastro") */
    fromCatalog = false;
    lastTypedName = '';
    descriptionText = '';
    private originalDescription: string | null = null;

    @ViewChildren(MatAutocompleteTrigger) autocompleteTriggers!: QueryList<MatAutocompleteTrigger>;

    private destroy$ = new Subject<void>();
    private openedByControl = new Map<FormControl, Subject<void>>();
    public pagedControllers = new Map<FormControl, PagedAutocompleteController<Product>>();
    public controllersData = new Map<FormControl, { results$: ReplaySubject<Product[]>, isLoadingMore$: ReplaySubject<boolean> }>();
    // ORCA-1975: as sugestões só aparecem com produtos que combinam com o nome (sem painel vazio/piscando)
    hasSuggestions = false;

    get itemNameControl(): FormControl | null {
        return this.form.get('item_name') as FormControl;
    }

    get itemProductIdControl(): FormControl | null {
        return this.form.get('product_id') as FormControl;
    }

    get isValueHidden(): boolean {
        return !!this.form.get('hide_value')?.value;
    }

    constructor(
        public matDialogRef: MatDialogRef<ProposalAddItemComponent>,
        private _localStorageService: LocalStorageService,
        @Inject(MAT_DIALOG_DATA) private _data: any,
        private _formBuilder: FormBuilder,
        private _singletonService: SingletonsService,
        private _productService: ProductsService,
        private _alertService: AlertService,
        private _pagedAutocompleteService: PagedAutocompleteService,
        private _proposalService: ProposalService,
    ) {
        this.index = this._data.index;
        this.item = this._data.item;
        if (this._data.fields) {
            this.fields = { ...this.fields, ...this._data.fields };
        }
        this.detailsOn = this._data.detailsOn !== false;
        this.canSaveProduct = this._data.canSaveProduct !== false;
        this.categories = this._data.categories || [];

        this.form = this._formBuilder.group({
            item_id: [this.item?.id],
            item_name: [this.item?.name, [Validators.required, Validators.maxLength(400)]],
            item_unit_id: [this.item?.unitId, [Validators.required]],
            item_per_unit: [this.item?.perUnit, [Validators.required]],
            item_quantity: [this.item?.quantity || 1, [Validators.required]],
            ordem: [this.item?.ordem || 1],
            should_save: [this.item?.shouldSave],
            product_id: [this.item?.productId],
            product_code: [(this.item as any)?.productCode || null],
            product_description: [(this.item as any)?.productDescription || null],
            product_category_name: [(this.item as any)?.productCategoryName || null],
            product_stock_quantity: [(this.item as any)?.productStockQuantity ?? null],
            product_stock_control_enabled: [(this.item as any)?.productStockControlEnabled ?? true],
            product_estoque_minimo: [(this.item as any)?.productEstoqueMinimo ?? null],
            product_image: [(this.item as any)?.productImage || null],
            product_unit: [(this.item as any)?.productUnit || null],
            hide_value: [this.item?.hideValue ?? false],
        });
        this.originalDescription = this.form.get('product_description')?.value ?? null;
        this.descriptionText = plainDescription(this.originalDescription);
        this.lastTypedName = this.item?.name || '';
        this.form.get('item_name')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            if (typeof value === 'string' && value !== NEW_ITEM_OPTION) {
                this.lastTypedName = value;
            }
        });
    }

    ngOnInit(): void {
        // Carrega unidades e default
        this.defaultUnit = this._localStorageService.getItem(Unit.defaultUnitKey);
        this._singletonService.getUnits().pipe(takeUntil(this.destroy$)).subscribe((units) => {
            this.units = units;
            this.defaultUnit = this.resolveDefaultUnit();
            // define unidade padrão se não houver
            if (this.defaultUnit && !this.form.get('item_unit_id')?.value) {
                this.form.get('item_unit_id')?.setValue(this.defaultUnit.id);
            }
        });
    }

    private normalizeUnitId(value: unknown): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private resolveDefaultUnit(): Unit | null {
        if (!this.units?.length) {
            return null;
        }

        const storedUnitId = this.normalizeUnitId(this.defaultUnit?.id);
        if (storedUnitId !== null) {
            const storedUnit = this.units.find(unit => this.normalizeUnitId(unit.id) === storedUnitId);
            if (storedUnit) {
                return storedUnit;
            }
        }

        return this.units.find(unit => this.normalizeUnitId(unit.id) === 1)
            || this.units.find(unit => unit.name === 'Unidade' && unit.abbr === 'Un')
            || this.units[0]
            || null;
    }

    ngAfterViewInit(): void {
        this.setupProductAutocomplete(this.form.get('item_name') as FormControl);
        // Só foca o nome ao adicionar. Ao editar, o foco abria as sugestões sozinho e escondia o Tipo.
        if (this.index < 0) {
            setTimeout(() => this.itemName?.nativeElement.focus(), 300);
        }
    }



    ngOnDestroy(): void {
        this.pagedControllers.forEach(controller => controller.destroy());
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Anexa listener de scroll ao abrir o autocomplete para infinite scroll */
    public onAutocompleteOpened(auto: MatAutocomplete, control: AbstractControl): void {
        const fc = control as FormControl;
        const openNotifier = this.openedByControl.get(fc);
        openNotifier?.next();
    }

    private setupProductAutocomplete(control: FormControl): void {
        const searchFn = (query: string, page: number) => this._productService.searchProductsByName(query, page);

        // Cria um Subject para notificar a abertura do painel
        const openNotifier = new Subject<void>();
        this.openedByControl.set(control, openNotifier);

        const controller = this._pagedAutocompleteService.create(
            control,
            searchFn,
            // A função de provedor de elemento é crucial, pois ela será executada
            // apenas quando o painel for aberto, garantindo a referência correta.
            () => {
                if (this.compact) {
                    return this.suggestionsList?.nativeElement ?? null;
                }
                return document.querySelector(`#${this.auto.id}.mat-autocomplete-panel`) as HTMLElement;
            },
            openNotifier
        );



        this.pagedControllers.set(control, controller);
        this.controllersData.set(control, {
            results$: new ReplaySubject<Product[]>(1),
            isLoadingMore$: new ReplaySubject<boolean>(1),
        });

        // Durante uma busca nova os resultados anteriores continuam na tela até chegar a resposta
        controller.results$.pipe(takeUntil(this.destroy$)).subscribe(results => {
            this.controllersData.get(control)?.results$.next(results);
            this.setHasSuggestions(results.length > 0);
        });

        controller.isLoadingMore$.pipe(takeUntil(this.destroy$)).subscribe(isLoadingMore => {
            this.controllersData.get(control)?.isLoadingMore$.next(isLoadingMore);
        });
    }

    setProduct(event: MatAutocompleteSelectedEvent): void {
        if (event.option.value === NEW_ITEM_OPTION) {
            this.keepTypedName();
            this.autoTrigger.closePanel();
            return;
        }
        this.applyProduct(event.option.value);
        this.autoTrigger.closePanel();
        this.itemName.nativeElement.blur();
    }

    // Sugestão escolhida na lista em linha (telas compactas)
    selectProduct(product: Product): void {
        this.applyProduct(product);
        this.suggestionsOpen = false;
        this.itemName.nativeElement.blur();
    }

    /** "Adicionar “…” como item novo": fica com o nome digitado e fecha as sugestões */
    keepTypedName(): void {
        this.form.get('item_name')?.setValue(this.lastTypedName);
        this.suggestionsOpen = false;
        this.itemName?.nativeElement.blur();
    }

    /** Estoque na busca de produtos (só com o controle de estoque ligado) — ORCA-1945 */
    productStock(product: Product): ProductStockInfo | null {
        const unitId = this.normalizeUnitId((product as any)?.unitId);
        return productStockInfo(product as any, {
            enabled: !!this._data?.stock?.enabled,
            minAlert: !!this._data?.stock?.minAlert,
            unitAbbr: unitId !== null ? this.getUnitById(unitId)?.abbr : null,
        });
    }

    productMeta(product: Product): string {
        const p: any = product || {};
        return [p.code ? 'Cód. ' + p.code : null, p.productCategoryName || null].filter(Boolean).join(' · ');
    }

    get categoryOptions(): string[] {
        return filterCategories(this.categories, this.form.get('product_category_name')?.value);
    }

    get isNewCategory(): boolean {
        return isNewCategory(this.categories, this.form.get('product_category_name')?.value);
    }

    onDescriptionInput(event: Event): void {
        this.descriptionText = (event.target as HTMLTextAreaElement).value;
        const text = this.descriptionText.trim();
        // Sem mudança no texto, mantém o HTML original do cadastro
        this.form.get('product_description')?.setValue(
            text === plainDescription(this.originalDescription) ? (this.originalDescription || null) : (text || null)
        );
    }

    uploadImage(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) {
            return;
        }
        if (!file.type.startsWith('image/')) {
            this._alertService.error('Escolha um arquivo de imagem (JPG, PNG ou WEBP).');
            return;
        }
        this.uploading = true;
        this._proposalService.uploadItemImage(file)
            .pipe(takeUntil(this.destroy$), finalize(() => this.uploading = false))
            .subscribe(
                (url) => this.form.get('product_image')?.setValue(url),
                (err) => this._alertService.error(err),
            );
    }

    onNameFocus(): void {
        if (this.compact) {
            this.openSuggestions();
        }
    }

    openSuggestions(): void {
        if (!this.compact || this.suggestionsOpen) {
            return;
        }
        this.suggestionsOpen = true;
        this.notifySuggestionsShown();
    }

    private setHasSuggestions(value: boolean): void {
        if (value === this.hasSuggestions) {
            return;
        }
        this.hasSuggestions = value;
        if (value && this.suggestionsOpen) {
            this.notifySuggestionsShown();
        }
    }

    // A lista entra no DOM no próximo ciclo (só quando há sugestões); aí liga a rolagem infinita nela
    private notifySuggestionsShown(): void {
        if (this.hasSuggestions) {
            setTimeout(() => this.openedByControl.get(this.itemNameControl as FormControl)?.next());
        }
    }

    closeSuggestions(event?: Event): void {
        if (!this.suggestionsOpen) {
            return;
        }
        // Esc fecha só as sugestões, não o diálogo
        event?.stopPropagation();
        this.suggestionsOpen = false;
    }

    // Foco em outro campo fecha as sugestões
    onFormFocusIn(event: FocusEvent): void {
        if (event.target !== this.itemName?.nativeElement) {
            this.suggestionsOpen = false;
        }
    }

    formatCurrency(value: number | string | null | undefined): string {
        const amount = Number(value);
        return (isNaN(amount) ? 0 : amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    private applyProduct(selected: Product): void {

        this.form.get('item_name')!
            .setValue(selected.title, { emitEvent: false });

        // Busca o nome da unidade do produto selecionado
        const unitId = this.normalizeUnitId(selected.unitId)
            ?? this.normalizeUnitId(this.defaultUnit?.id);
        const productUnitName = unitId ? this.getUnitNameById(unitId) : null;

        this.form.patchValue({
            should_save: null,
            product_id: selected.id,
            item_unit_id: unitId,
            item_per_unit: selected.valor,
            product_code: selected.code || null,
            product_description: selected.description || null,
            product_category_name: selected.productCategoryName || null,
            product_stock_quantity: selected.stockQuantity ?? null,
            product_stock_control_enabled: selected.stockControlEnabled !== false,
            product_estoque_minimo: selected.estoqueMinimo ?? null,
            product_image: selected.image || null,
            product_unit: productUnitName || null,
        });
        this.originalDescription = selected.description || null;
        this.descriptionText = plainDescription(this.originalDescription);
        this.fromCatalog = true;
    }

    onIconClick(event: MouseEvent): void {
        event.stopPropagation();
        const current = this.form.get('should_save')?.value;
        this.form.get('should_save')?.setValue(!current);
        this.showFeedBackItemSaveMessage(!current);
    }

    showFeedBackItemSaveMessage(isSaving: boolean): void {
        if (isSaving) {
            this._alertService.success('O item será salvo na sua lista de produtos.');
        } else {
            this._alertService.success('O item não será salvo na sua lista de produtos.');
        }
    }

    cancel(): void {
        this.matDialogRef.close({ action: 'cancel' });
    }

    saveItem(): void {
        const values = this.form.value;
        if (!values.item_unit_id && this.defaultUnit) {
            values.item_unit_id = this.defaultUnit.id;
        }
        
        // Se não tiver product_unit preenchido, busca o nome da unidade atual
        if (!values.product_unit && values.item_unit_id) {
            values.product_unit = this.getUnitNameById(values.item_unit_id);
        }

        this.matDialogRef.close({
            action: 'save',
            item: Object.assign(new Item(), {
                id: values.item_id, 
                name: values.item_name,
                unitId: values.item_unit_id,
                perUnit: values.item_per_unit,
                quantity: values.item_quantity,
                productId: values.product_id,
                shouldSave: values.should_save,
                productCode: values.product_code,
                productDescription: values.product_description,
                productCategoryName: values.product_category_name,
                productStockQuantity: values.product_stock_quantity,
                productStockControlEnabled: values.product_stock_control_enabled,
                productEstoqueMinimo: values.product_estoque_minimo,
                productImage: values.product_image,
                productUnit: values.product_unit,
                hideValue: values.hide_value,
            }),
            index: this.index,
        });
        
    }

    getUnitById(id: number) {
        return this.units.find((u) => u.id === id) || null;
    }

    getUnitNameById(id: number): string | undefined {
        return this.getUnitById(id)?.name;
    }

    getUnitSuffixById(id: number): string | undefined {
        const unit = this.getUnitById(id);
        if (unit?.type === Unit.TYPE_PERCENT) {
            return unit.abbr;
        }
        return undefined;
    }

    getTotalValue(): number {
        const values = this.form.value;
        const unit = this.getUnitById(values.item_unit_id);
        if (!unit) return 0;
        if (unit.type === Unit.TYPE_PERCENT && values.item_per_unit > 0) {
            return (values.item_per_unit / 100) * values.item_quantity;
        }
        return values.item_per_unit * values.item_quantity;
    }

    getTotal(): string {
        return this.getTotalValue().toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
        });
    }

    removeItem(): void {
        // implementar se necessário
    }
}
